import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  MapPin,
  Check,
  Cpu,
  Activity,
  Radio,
  Send,
  UserCheck,
  Building2,
  Sparkles,
} from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useReveal } from '../hooks/useReveal';
import { CONTACT_EMAIL } from '../data/divisions';
import { CTASection } from '../components/CTASection';
import './ContactPage.css';

const LAT = 52.422214236294984;
const LNG = 16.973055509240254;

export function ContactPage() {
  const { t, language } = useI18n();
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formSent, setFormSent] = useState(false);

  useReveal(['contact.name', language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSent(true);
    setTimeout(() => setFormSent(false), 5000);
    setFormState({ name: '', email: '', message: '' });
  };

  const mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${LNG - 0.008}%2C${LAT - 0.005}%2C${LNG + 0.008}%2C${LAT + 0.005}&layer=mapnik&marker=${LAT}%2C${LNG}`;

  return (
    <div className="contact-page accent-contact">
      {/* Hero Header */}
      <header className="dp-hero">
        <span className="dp-hero-glow" aria-hidden="true" />
        <div className="dp-hero-inner">
          <Link to="/" className="dp-back reveal">
            <ArrowLeft size={16} />
            <span>{t('common.backHome')}</span>
          </Link>

          <div className="dp-hero-head reveal">
            <span className="dp-icon">
              <Mail size={28} />
            </span>
            <div>
              <div className="dp-name">{t('contact.name')}</div>
              <span className="tile-badge">{t('contact.badge')}</span>
            </div>
          </div>

          <h1 className="dp-title reveal">{t('contact.heroTitle')}</h1>
          <p className="dp-lead reveal">{t('contact.heroLead')}</p>

          <div className="dp-specs reveal">
            <div className="dp-spec">
              <span>Siedziba / HQ</span>
              <strong>Poznań, Główna</strong>
            </div>
            <div className="dp-spec">
              <span>Tryb pracy</span>
              <strong>24h Nadzór AI + Maszyny</strong>
            </div>
          </div>
        </div>
      </header>

      {/* Section 1: O nas (About Us) & 24h Supervision */}
      <section className="section cp-section">
        <div className="section-eyebrow reveal">{t('contact.aboutTitle')}</div>
        <div className="cp-about-grid">
          <div className="cp-card cp-about-card reveal">
            <div className="cp-card-icon">
              <Building2 size={24} />
            </div>
            <h2>{t('contact.aboutTitle')}</h2>
            <p className="cp-about-statement">{t('contact.aboutText')}</p>
          </div>

          <div className="cp-card cp-supervision-card reveal">
            <div className="cp-supervision-header">
              <span className="cp-live-pulse" />
              <span className="cp-live-badge">SYSTEM ACTIVE 24/7</span>
            </div>
            <h3>Autonomiczny Nadzór Studia</h3>
            <p className="cp-supervision-lead">
              Ludzki inżynier sprawuje wyłącznie wyższy nadzór nad maszynami pracującymi 24h.
            </p>
            <ul className="cp-supervision-list">
              <li>
                <Activity size={18} className="cp-list-icon" />
                <span>Narzędzia alertująco-monitorujące 24h</span>
              </li>
              <li>
                <Radio size={18} className="cp-list-icon" />
                <span>Interfejs komunikacji z maszynami</span>
              </li>
              <li>
                <Cpu size={18} className="cp-list-icon" />
                <span>Równoległy rozwój oprogramowania z kilku branż</span>
              </li>
            </ul>
            <div className="cp-branches-pills">
              <span className="cp-branch-pill">AI & Agentic Systems</span>
              <span className="cp-branch-pill">GameDev Unity 6</span>
              <span className="cp-branch-pill">Avionics & Drone Swarms</span>
              <span className="cp-branch-pill">Teach&Learn AI</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: O autorze i korzeniach firmy */}
      <section className="section cp-section">
        <div className="section-eyebrow reveal">{t('contact.originTitle')}</div>
        <div className="cp-author-grid">
          {/* Author photo card */}
          <div className="cp-author-card reveal">
            <div className="cp-author-image-wrapper">
              <img
                src="/author.png"
                alt="Autor Skycodee"
                className="cp-author-image"
                loading="eager"
              />
              <div className="cp-author-overlay">
                <span className="cp-author-badge">
                  <UserCheck size={14} />
                  <span>{t('contact.authorTitle')}</span>
                </span>
              </div>
            </div>
            <div className="cp-author-info">
              <h3>Lead Engineer & Founder</h3>
              <p className="cp-author-role">{t('contact.authorTagline')}</p>
            </div>
          </div>

          {/* Origins description card */}
          <div className="cp-card cp-origins-card reveal">
            <div className="cp-card-icon">
              <Sparkles size={24} />
            </div>
            <h2>{t('contact.originTitle')}</h2>
            <p className="cp-origin-text">{t('contact.originText')}</p>

            <div className="cp-origin-details">
              <div className="cp-detail-box">
                <MapPin size={20} className="cp-detail-icon" />
                <div>
                  <span className="cp-detail-label">Lokalizacja / Location</span>
                  <strong>Główna, Poznań, Polska</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Interactive Map with Pin "Siedziba Skycode" */}
      <section className="section cp-section">
        <div className="section-eyebrow reveal">{t('contact.mapTitle')}</div>
        <div className="cp-map-wrapper reveal">
          <div className="cp-map-header">
            <div>
              <h2>{t('contact.mapTitle')}</h2>
              <p>{t('contact.mapSubtitle')}</p>
            </div>
          </div>

          <div className="cp-map-container">
            <iframe
              title="Siedziba Skycode Pin Map"
              src={mapEmbedUrl}
              className="cp-map-iframe"
              loading="lazy"
            />
            {/* Custom Styled Pin Card Overlay */}
            <div className="cp-map-pin-overlay">
              <div className="cp-pin-badge">
                <MapPin size={22} className="cp-pin-icon" />
                <div>
                  <strong>Siedziba Skycode</strong>
                  <span>Główna, Poznań</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Contact Options & Form */}
      <section className="section cp-section">
        <div className="section-eyebrow reveal">{t('contact.directContact')}</div>
        <div className="cp-contact-methods">
          <a href={`mailto:${CONTACT_EMAIL}`} className="cp-card cp-method-card reveal">
            <Mail size={32} className="cp-method-icon" />
            <div className="cp-method-content">
              <span className="cp-method-label">{t('contact.directContact')}</span>
              <h3 className="cp-method-email">{CONTACT_EMAIL}</h3>
              <p className="cp-method-desc">{t('contact.emailUs')}</p>
            </div>
          </a>

          <div className="cp-card cp-form-card reveal">
            <h3>{t('contact.sendMessage')}</h3>
            {formSent ? (
              <div className="cp-form-success">
                <Check size={28} />
                <p>Wiadomość została zrealizowana! Dziękujemy za kontakt.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="cp-form">
                <div className="cp-form-row">
                  <input
                    type="text"
                    placeholder={t('contact.formName') as string || 'Twoje imię'}
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    required
                    className="cp-input"
                  />
                  <input
                    type="email"
                    placeholder={t('contact.formEmail') as string || 'Twój e-mail'}
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    required
                    className="cp-input"
                  />
                </div>
                <textarea
                  placeholder={t('contact.formMessage') as string || 'Wpisz treść wiadomości...'}
                  value={formState.message}
                  onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                  required
                  rows={4}
                  className="cp-textarea"
                />
                <button type="submit" className="cp-submit-btn">
                  <Send size={18} />
                  <span>{t('contact.sendMessage')}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
